'use client';

/**
 * TransitionArrow Component
 *
 * SVG arrow connecting pipeline stages with animated flow direction
 * and optional neon glow effect.
 */

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import type React from 'react';
import { useId } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { neonAccents } from '../../theme/palette';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface TransitionArrowProps {
  /** Neon accent color for the arrow */
  neonAccent?: NeonAccent;
  /** Whether the arrow is animated */
  animated?: boolean;
  /** Arrow size variant */
  size?: 'small' | 'medium' | 'large';
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const sizeConfig = {
  small: {
    width: 24,
    height: 16,
    strokeWidth: 1.5,
  },
  medium: {
    width: 32,
    height: 20,
    strokeWidth: 2,
  },
  large: {
    width: 40,
    height: 24,
    strokeWidth: 2.5,
  },
} as const;

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $width: number;
  $height: number;
}

const StyledContainer = styled(
  Box,
  transientOptions,
)<StyledContainerProps>(({ $width, $height }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: $width,
  height: $height,
  flexShrink: 0,
}));

// ============================================================================
// Component
// ============================================================================

export const TransitionArrow: React.FC<TransitionArrowProps> = ({
  neonAccent = 'cyan',
  animated = true,
  size = 'medium',
  className,
}) => {
  const uid = useId();
  const filterId = `glow-${uid}`;
  const gradientId = `arrow-gradient-${uid}`;

  const config = sizeConfig[size];
  const colors = neonAccents[neonAccent];
  const strokeColor = colors.base;
  const glowColor = colors.glow;

  // Animation variants for the flowing dots
  const dotVariants = {
    animate: {
      x: [0, config.width - 8],
      opacity: [0, 1, 1, 0],
    },
  };

  return (
    <StyledContainer $width={config.width} $height={config.height} className={className}>
      <svg
        width={config.width}
        height={config.height}
        viewBox={`0 0 ${config.width} ${config.height}`}
        fill="none"
        style={{ overflow: 'visible' }}
      >
        {/* Glow filter */}
        <defs>
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Gradient for the arrow */}
          <linearGradient id={gradientId} x1="0%" y1="50%" x2="100%" y2="50%">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.3" />
            <stop offset="50%" stopColor={strokeColor} stopOpacity="1" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0.5" />
          </linearGradient>
        </defs>

        {/* Main arrow line */}
        <line
          x1="0"
          y1={config.height / 2}
          x2={config.width - 6}
          y2={config.height / 2}
          stroke={`url(#${gradientId})`}
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
          filter={`url(#${filterId})`}
        />

        {/* Arrow head */}
        <polyline
          points={`${config.width - 10},${config.height / 2 - 4} ${config.width - 4},${config.height / 2} ${config.width - 10},${config.height / 2 + 4}`}
          stroke={strokeColor}
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          filter={`url(#${filterId})`}
        />

        {/* Animated flowing dot */}
        {animated && (
          <motion.circle
            cx="4"
            cy={config.height / 2}
            r={config.strokeWidth}
            fill={strokeColor}
            style={{
              filter: `drop-shadow(0 0 4px ${glowColor})`,
            }}
            variants={dotVariants}
            animate="animate"
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatDelay: 0.5,
            }}
          />
        )}
      </svg>
    </StyledContainer>
  );
};

TransitionArrow.displayName = 'TransitionArrow';

export default TransitionArrow;
